import { useState } from 'react'
import {
  Root,
  Trigger,
  Portal,
  Content,
  PopoverTriggerProps,
} from '@radix-ui/react-popover'
import {
  addYears,
  endOfDecade,
  isAfter,
  isBefore,
  isSameMonth,
  isSameYear,
  startOfDecade,
} from 'date-fns'
import { formatDateClient } from 'shared/datetime'
import { cn } from 'shared/styles'
import { ElementPicker } from './ElementPicker'
import { Predicate } from './types/Condition'
import { Decade } from './types/Decade'
import { Mode } from './types/Mode'

const DECADES = Array.from({ length: 12 }, (_, index) => {
  const date = addYears(new Date(), (index - 6) * 10)

  return { from: startOfDecade(date), to: endOfDecade(date) }
})

const isInDecadeYear = (year: Date, decade: Decade) =>
  isBefore(decade.from, year) && isAfter(decade.to, year)

type Props = {
  month: Date
  setMonth: (newMonth: Date) => void
  disabledDates?: Predicate[]
} & PopoverTriggerProps

export function MonthPicker({
  disabledDates,
  month: externalMonth,
  setMonth: setExternalMonth,
  ...props
}: Props) {
  const [mode, setMode] = useState<Mode>('month')
  const [innerMonth, setInnerMonth] = useState<Date>(externalMonth)
  const [decade, setDecade] = useState<Decade>({
    from: startOfDecade(innerMonth),
    to: endOfDecade(innerMonth),
  })
  const [isOpen, setIsOpen] = useState(false)

  const years = Array.from(
    { length: 10 },
    (_, index) => new Date(decade.from.getFullYear() + index, 0),
  )

  const months = Array.from(
    { length: 12 },
    (_, index) => new Date(innerMonth.getFullYear(), index),
  )

  const handleMonthChange = (month: Date) => {
    setInnerMonth(month)
    setExternalMonth(month)
    setIsOpen(false)
  }

  const handleYearChange = (year: Date) => {
    setMode('month')
    setInnerMonth(year)
  }

  const handleDecadeClick = (newDecade: Decade) => {
    setMode('year')
    setDecade(newDecade)
  }

  const isDisabledDecade = (decade: Decade, disabled: Predicate[]) =>
    disabled.some((predicate) =>
      Array.from({ length: 10 }).every((_, index) =>
        predicate(addYears(decade.from, index)),
      ),
    )

  return (
    <Root
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Trigger
        {...props}
        className={cn(
          'base-input border px-4 py-2 transition',
          props.className,
        )}
      >
        {formatDateClient(externalMonth, 'LLLL yyyy')}
      </Trigger>

      <Portal>
        <Content className='animate-enterFromTop z-1050 mt-1 flex min-w-(--radix-popover-trigger-width) flex-col gap-2 rounded-md border bg-white p-4 shadow-lg'>
          {mode === 'month' && (
            <ElementPicker
              elements={months}
              headerText={innerMonth.getFullYear().toString()}
              isSelectedElement={(month: Date) =>
                isSameMonth(externalMonth, month)
              }
              makeElementText={(month) => formatDateClient(month, 'LLL')}
              onElementClick={(month) => handleMonthChange(month)}
              onHeaderClick={() => setMode('year')}
              disabledDates={
                disabledDates &&
                ((month) => disabledDates.some((predicate) => predicate(month)))
              }
            />
          )}

          {mode === 'year' && (
            <ElementPicker
              elements={years}
              headerText={`${decade.from.getFullYear()} - ${decade.to.getFullYear()}`}
              isSelectedElement={(year) => isSameYear(externalMonth, year)}
              makeElementText={(year) => year.getFullYear().toString()}
              onElementClick={(year) => handleYearChange(year)}
              onHeaderClick={() => setMode('decade')}
              disabledDates={
                disabledDates &&
                ((year) => disabledDates.some((predicate) => predicate(year)))
              }
            />
          )}

          {mode === 'decade' && (
            <ElementPicker
              elements={DECADES}
              headerText={`${decade.from.getFullYear()} - ${decade.to.getFullYear()}`}
              isSelectedElement={(decade) =>
                isInDecadeYear(externalMonth, decade)
              }
              makeElementText={(decade) =>
                `${formatDateClient(decade.from, 'yyyy')}-${formatDateClient(decade.to, 'yy')}`
              }
              onElementClick={handleDecadeClick}
              disabledDates={
                disabledDates &&
                ((decade) => isDisabledDecade(decade, disabledDates))
              }
            />
          )}
        </Content>
      </Portal>
    </Root>
  )
}
